import { useEffect, useRef } from "react";
import "./RockBombAnimation.css";

export default function RockBombAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for attacker and defender
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Create floating rocks around the attacker
    const rocks = [];
    for (let i = 0; i < 5; i++) {
      const rock = document.createElement("div");
      rock.className = "rock-bomb-rock";
      
      // Randomize size
      const size = 15 + Math.random() * 20;
      rock.style.width = `${size}px`;
      rock.style.height = `${size}px`;
      
      // Position around attacker
      const angle = (i / 5) * Math.PI * 2;
      const radius = 40;
      const x = attackerX + Math.cos(angle) * radius;
      const y = attackerY + Math.sin(angle) * radius;
      
      rock.style.left = `${x}px`;
      rock.style.top = `${y}px`;
      
      // Randomize appearance
      rock.style.setProperty('--rock-rotation', `${Math.random() * 360}deg`);
      rock.style.setProperty('--rock-color', Math.random() > 0.5 ? '#736654' : '#8b7b6a');
      
      container.appendChild(rock);
      rocks.push(rock);
      
      // Orbit animation for each rock
      setTimeout(() => {
        rock.style.animation = `rockOrbit 0.8s ease-in-out forwards`;
        rock.style.setProperty('--target-x', `${defenderX}px`);
        rock.style.setProperty('--target-y', `${defenderY}px`);
      }, i * 100);
    }
    
    // Create main rock bomb
    const rockBomb = document.createElement("div");
    rockBomb.className = "rock-bomb-main";
    rockBomb.style.left = `${attackerX}px`;
    rockBomb.style.top = `${attackerY}px`;
    rockBomb.style.setProperty('--target-x', `${defenderX}px`);
    rockBomb.style.setProperty('--target-y', `${defenderY}px`);
    container.appendChild(rockBomb);
    animRef.current = rockBomb;
    
    // Launch after the small rocks start moving
    setTimeout(() => {
      rockBomb.classList.add("launch");
    }, 300);
    
    // Explosion effect when the bomb hits
    setTimeout(() => {
      // Remove the main rock bomb
      rockBomb.remove();
      
      // Create explosion
      const explosion = document.createElement("div");
      explosion.className = "rock-bomb-explosion";
      explosion.style.left = `${defenderX}px`;
      explosion.style.top = `${defenderY}px`;
      container.appendChild(explosion);
      
      // Create rock fragments
      for (let i = 0; i < 12; i++) {
        const fragment = document.createElement("div");
        fragment.className = "rock-bomb-fragment";
        
        // Size and style
        const size = 5 + Math.random() * 15;
        fragment.style.width = `${size}px`;
        fragment.style.height = `${size}px`;
        fragment.style.setProperty('--rock-color', Math.random() > 0.5 ? '#736654' : '#8b7b6a');
        
        // Position at impact point
        fragment.style.left = `${defenderX}px`;
        fragment.style.top = `${defenderY}px`;
        
        // Direction to fly
        const angle = Math.random() * Math.PI * 2;
        const distance = 30 + Math.random() * 60;
        fragment.style.setProperty('--frag-angle', angle + 'rad');
        fragment.style.setProperty('--frag-distance', distance + 'px');
        
        container.appendChild(fragment);
        
        // Auto-remove fragments
        setTimeout(() => fragment.remove(), 1000 + Math.random() * 200);
      }
      
      // Shake and flash the defender
      defenderEl.classList.add('rock-bomb-hit');
      setTimeout(() => {
        defenderEl.classList.remove('rock-bomb-hit');
      }, 400);
      
      // Remove explosion after animation
      setTimeout(() => explosion.remove(), 500);
    }, 850);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      // Clean up any remaining elements
      rocks.forEach(rock => {
        if (rock.parentNode) rock.remove();
      });
      
      if (rockBomb.parentNode) rockBomb.remove();
      
      onComplete();
    }, 1500);

    // Cleanup function
    return () => {
      clearTimeout(completeTimer);
      
      rocks.forEach(rock => {
        if (rock.parentNode) rock.remove();
      });
      
      if (rockBomb && rockBomb.parentNode) {
        rockBomb.remove();
      }
      
      if (defenderEl) {
        defenderEl.classList.remove('rock-bomb-hit');
      }
      
      // Remove any remaining elements
      const elements = container.querySelectorAll('.rock-bomb-explosion, .rock-bomb-fragment');
      elements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);

  return null;
}

RockBombAnimation.moveType = "rock";
